import { sql } from "drizzle-orm";
import {
	index,
	integer,
	primaryKey,
	sqliteTable,
	text,
} from "drizzle-orm/sqlite-core";

export type WorkspaceStatus = "active" | "suspended";

export type AdminTenantView = {
	id: string;
	name: string;
	slug: string;
	status: string;
	createdAt: string;
	updatedAt: string;
};

export const workspaces = sqliteTable("workspaces", {
	id: text("id").primaryKey(),
	name: text("name").notNull(),
	slug: text("slug").notNull().unique(),
	dbUrl: text("db_url").notNull(),
	dbToken: text("db_token").notNull(),
	status: text("status").notNull().default("active"),
	stripeCustomerId: text("stripe_customer_id"),
	subscriptionStatus: text("subscription_status").notNull().default("none"),
	planTier: text("plan_tier").notNull().default("free"),
	stripeSubscriptionId: text("stripe_subscription_id"),
	paymentFailure: integer("payment_failure").notNull().default(0),
	currentPeriodEnd: text("current_period_end"),
	gracePeriodEnd: text("grace_period_end"),
	trialEnd: text("trial_end"),
	createdAt: text("created_at").notNull().default(sql`CURRENT_TIMESTAMP`),
	updatedAt: text("updated_at").notNull().default(sql`CURRENT_TIMESTAMP`),
});

export const stripeEventsLog = sqliteTable("stripe_events_log", {
	eventId: text("event_id").primaryKey(),
	eventType: text("event_type").notNull(),
	processedAt: text("processed_at").notNull().default(sql`CURRENT_TIMESTAMP`),
});

export const systemHealth = sqliteTable("system_health", {
	id: integer("id").primaryKey(),
	checkedAt: text("checked_at").notNull().default(sql`CURRENT_TIMESTAMP`),
});

export const platformAdmins = sqliteTable("platform_admins", {
	email: text("email").primaryKey(),
	name: text("name"),
	role: text("role").notNull().default("admin"),
	createdAt: text("created_at").notNull().default(sql`CURRENT_TIMESTAMP`),
	updatedAt: text("updated_at").notNull().default(sql`CURRENT_TIMESTAMP`),
});

export const adminUser = sqliteTable("admin_user", {
	id: text("id").primaryKey(),
	name: text("name").notNull(),
	email: text("email").notNull().unique(),
	emailVerified: integer("email_verified", { mode: "boolean" })
		.notNull()
		.default(false),
	image: text("image"),
	createdAt: integer("created_at", { mode: "timestamp" }).notNull(),
	updatedAt: integer("updated_at", { mode: "timestamp" }).notNull(),
});

export const adminSession = sqliteTable("admin_session", {
	id: text("id").primaryKey(),
	expiresAt: integer("expires_at", { mode: "timestamp" }).notNull(),
	token: text("token").notNull().unique(),
	ipAddress: text("ip_address"),
	userAgent: text("user_agent"),
	userId: text("user_id")
		.notNull()
		.references(() => adminUser.id, { onDelete: "cascade" }),
	createdAt: integer("created_at", { mode: "timestamp" }).notNull(),
	updatedAt: integer("updated_at", { mode: "timestamp" }).notNull(),
});

export const adminAccount = sqliteTable("admin_account", {
	id: text("id").primaryKey(),
	accountId: text("account_id").notNull(),
	providerId: text("provider_id").notNull(),
	userId: text("user_id")
		.notNull()
		.references(() => adminUser.id, { onDelete: "cascade" }),
	accessToken: text("access_token"),
	refreshToken: text("refresh_token"),
	idToken: text("id_token"),
	accessTokenExpiresAt: integer("access_token_expires_at", {
		mode: "timestamp",
	}),
	refreshTokenExpiresAt: integer("refresh_token_expires_at", {
		mode: "timestamp",
	}),
	scope: text("scope"),
	password: text("password"),
	createdAt: integer("created_at", { mode: "timestamp" }).notNull(),
	updatedAt: integer("updated_at", { mode: "timestamp" }).notNull(),
});

export const impersonationLog = sqliteTable("impersonation_log", {
	id: text("id").primaryKey(),
	adminId: text("admin_id").notNull(),
	targetUserId: text("target_user_id").notNull(),
	tenantSlug: text("tenant_slug").notNull(),
	action: text("action").notNull(), // 'start' | 'stop'
	createdAt: text("created_at").notNull().default(sql`CURRENT_TIMESTAMP`),
});

export const adminAuditLog = sqliteTable(
	"audit_log",
	{
		id: text("id").primaryKey(),
		eventType: text("event_type").notNull(),
		actorId: text("actor_id").notNull(),
		actorIp: text("actor_ip"),
		timestamp: text("timestamp").notNull().default(sql`CURRENT_TIMESTAMP`),
		resourceType: text("resource_type"),
		resourceId: text("resource_id"),
		tenantSlug: text("tenant_slug"),
		actionDetails: text("action_details"),
		method: text("method"),
		path: text("path"),
		userAgent: text("user_agent"),
		responseStatus: integer("response_status"),
	},
	(table) => [
		index("audit_log_timestamp_idx").on(table.timestamp),
		index("audit_log_event_type_idx").on(table.eventType),
		index("audit_log_actor_id_idx").on(table.actorId),
	],
);

export type ChangelogCategory = "feature" | "fix" | "improvement";

export const changelogEntries = sqliteTable(
	"changelog_entries",
	{
		id: text("id").primaryKey(),
		title: text("title").notNull(),
		slug: text("slug").notNull().unique(),
		body: text("body").notNull(),
		version: text("version"),
		publishedAt: text("published_at"),
		category: text("category").notNull(),
		createdAt: text("created_at").notNull().default(sql`CURRENT_TIMESTAMP`),
		updatedAt: text("updated_at").notNull().default(sql`CURRENT_TIMESTAMP`),
	},
	(table) => [index("changelog_published_at_idx").on(table.publishedAt)],
);

export const jobStatus = sqliteTable(
	"job_status",
	{
		id: text("id").primaryKey(),
		type: text("type").notNull(),
		status: text("status").notNull().default("pending"),
		tenantSlug: text("tenant_slug"),
		requestId: text("request_id"),
		enqueuedAt: text("enqueued_at").notNull(),
		startedAt: text("started_at"),
		completedAt: text("completed_at"),
		error: text("error"),
		result: text("result"),
		attempts: integer("attempts").notNull().default(0),
	},
	(table) => [
		index("job_status_type_idx").on(table.type),
		index("job_status_status_idx").on(table.status),
	],
);

export const adminVerification = sqliteTable("admin_verification", {
	id: text("id").primaryKey(),
	identifier: text("identifier").notNull(),
	value: text("value").notNull(),
	expiresAt: integer("expires_at", { mode: "timestamp" }).notNull(),
	createdAt: integer("created_at", { mode: "timestamp" }),
	updatedAt: integer("updated_at", { mode: "timestamp" }),
});

export const platformUser = sqliteTable("platform_user", {
	id: text("id").primaryKey(),
	name: text("name").notNull(),
	email: text("email").notNull().unique(),
	emailVerified: integer("email_verified", { mode: "boolean" })
		.notNull()
		.default(false),
	image: text("image"),
	tosVersionAccepted: text("tos_version_accepted"),
	tosAcceptedAt: integer("tos_accepted_at", { mode: "timestamp" }),
	createdAt: integer("created_at", { mode: "timestamp" }).notNull(),
	updatedAt: integer("updated_at", { mode: "timestamp" }).notNull(),
});

export const platformSession = sqliteTable("platform_session", {
	id: text("id").primaryKey(),
	expiresAt: integer("expires_at", { mode: "timestamp" }).notNull(),
	token: text("token").notNull().unique(),
	ipAddress: text("ip_address"),
	userAgent: text("user_agent"),
	userId: text("user_id")
		.notNull()
		.references(() => platformUser.id, { onDelete: "cascade" }),
	createdAt: integer("created_at", { mode: "timestamp" }).notNull(),
	updatedAt: integer("updated_at", { mode: "timestamp" }).notNull(),
});

export const platformAccount = sqliteTable("platform_account", {
	id: text("id").primaryKey(),
	accountId: text("account_id").notNull(),
	providerId: text("provider_id").notNull(),
	userId: text("user_id")
		.notNull()
		.references(() => platformUser.id, { onDelete: "cascade" }),
	accessToken: text("access_token"),
	refreshToken: text("refresh_token"),
	idToken: text("id_token"),
	accessTokenExpiresAt: integer("access_token_expires_at", {
		mode: "timestamp",
	}),
	refreshTokenExpiresAt: integer("refresh_token_expires_at", {
		mode: "timestamp",
	}),
	scope: text("scope"),
	password: text("password"),
	createdAt: integer("created_at", { mode: "timestamp" }).notNull(),
	updatedAt: integer("updated_at", { mode: "timestamp" }).notNull(),
});

export const platformVerification = sqliteTable("platform_verification", {
	id: text("id").primaryKey(),
	identifier: text("identifier").notNull(),
	value: text("value").notNull(),
	expiresAt: integer("expires_at", { mode: "timestamp" }).notNull(),
	createdAt: integer("created_at", { mode: "timestamp" }),
	updatedAt: integer("updated_at", { mode: "timestamp" }),
});

export const invitationTokens = sqliteTable("invitation_tokens", {
	token: text("token").primaryKey(),
	workspaceId: text("workspace_id")
		.notNull()
		.references(() => workspaces.id, { onDelete: "cascade" }),
	email: text("email").notNull(),
	createdAt: text("created_at").notNull().default(sql`CURRENT_TIMESTAMP`),
});

export const onboardingProgress = sqliteTable("onboarding_progress", {
	platformUserId: text("platform_user_id")
		.primaryKey()
		.references(() => platformUser.id, { onDelete: "cascade" }),
	completedSteps: text("completed_steps").notNull().default("[]"),
	currentStep: text("current_step"),
	dismissed: integer("dismissed").notNull().default(0),
	completed: integer("completed").notNull().default(0),
	createdAt: text("created_at").notNull().default(sql`CURRENT_TIMESTAMP`),
	updatedAt: text("updated_at").notNull().default(sql`CURRENT_TIMESTAMP`),
});

export const workspaceMembers = sqliteTable(
	"workspace_members",
	{
		platformUserId: text("platform_user_id")
			.notNull()
			.references(() => platformUser.id, { onDelete: "cascade" }),
		workspaceId: text("workspace_id")
			.notNull()
			.references(() => workspaces.id, { onDelete: "cascade" }),
		role: text("role").notNull().default("member"),
		createdAt: integer("created_at", { mode: "timestamp" }).notNull(),
	},
	(table) => [
		primaryKey({ columns: [table.platformUserId, table.workspaceId] }),
	],
);
